export type AddEdit<T> = { [P in keyof T]: T[P] | null };

export type Diff<T extends keyof any, U extends keyof any> = ({ [P in T]: P } & { [P in U]: never })[T];

/**
 * Types the specific entries with the specific typed keys instead of string.
 * Only use Entries/getObjectEntries when you know that the values will have no excess properties; Typescript won't check this for you.
 */
export type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

export const getObjectEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

export type MakeGivenKeysOptional<T, K extends keyof T> = Partial<T> & Omit<T, K>;

/**
 * Sets null as possible value for all object keys that are primitives (i.e. not arrays or objects).
 * `Date` is also made nullable, because it is close to being a primitive value.
 */
export type NullablePrimitives<T extends object> = {
    [Key in keyof T]: T[Key] extends (infer U)[]
        ? U[]
        : T[Key] extends object
          ? T[Key] extends Date
              ? T[Key] | null
              : T[Key]
          : T[Key] | null;
};

export type Nullable<T> = {
    [P in keyof T]: T[P] | null;
};

export type NullablePartial<T> = { [P in keyof T]?: T[P] | null };

/**
 * Recursively removes `null` and `undefined` typing from all (nested) properties of the object.
 * Making everything non-nullable.
 *
 * @example
 * ```
 * type FooObject = {
 *   foo: 'bar' | null;
 * };
 *
 * const fooObject: FooObject = {
 *   foo: null;
 * }
 *
 * type FooObjectNonNullable = NonNullableRecursive<FooObject>;
 *
 * const myAssertedObject: FooObjectNonNullable = {
 *  foo: null // TypeError: Type 'null' is not assignable to type 'string'.
 * }
 * ```
 */
export type NonNullableRecursive<T> = {
    [P in keyof T]: T[P] extends object ? NonNullableRecursive<NonNullable<T[P]>> : NonNullable<T[P]>;
};

export type Override<T, U> = Pick<T, Diff<keyof T, keyof U>> & U;

/**
 * From https://stackoverflow.com/questions/49401866/all-possible-keys-of-an-union-type
 * If T is a union type, it will return the keys of all possible union values.
 */
export type KeysOfUnion<T> = T extends T ? keyof T : never;

// Deep partial with a hardcoded (arbitrary) maximum depth of 5, which prevents infinite recursion
export type DeepPartial<T, CurrentDepth extends number[] = []> = CurrentDepth['length'] extends 5
    ? T
    : T extends object
      ? {
            [P in keyof T]?: DeepPartial<T[P], [...CurrentDepth, 1]>;
        }
      : T;

// Deep partial with a hardcoded (arbitrary) maximum depth of 5, which prevents infinite recursion
export type DeepNullable<T, CurrentDepth extends number[] = []> = CurrentDepth['length'] extends 5
    ? T
    : T extends object
      ? {
            [P in keyof T]: DeepNullable<T[P], [...CurrentDepth, 1]>;
        }
      : T | null;

/**
 * Make all properties defined by `RequiredKeys` required and make the rest optional.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PartialRequired<T extends Record<string, any>, RequiredKeys extends keyof T> = Partial<T> &
    Required<Pick<T, RequiredKeys>>;

/**
 * Make all properties defined by `NonNullableKeys` non-nullable and make the reset optional.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PartialNonNullable<T extends Record<string, any>, NonNullableKeys extends keyof T> = Partial<T> & {
    [P in keyof Pick<T, NonNullableKeys>]-?: NonNullable<T[P]>;
};

/**
 * Replaces the types of specified properties in an object type.
 *
 * @template Object - The original object type.
 * @template Updates - An object type that specifies the properties to update and their new types.
 *
 * @remarks
 * This utility type takes two type parameters: `Object` and `Updates`. It omits the properties
 * from `Object` that are present in `Updates` and then merges the remaining properties with the
 * properties from `Updates`, effectively replacing the types of the specified properties.
 *
 * @example
 * ```typescript
 * type Original = { a: number; b: string; c: boolean };
 * type Updates = { b: number; c: string };
 * type Result = ReplacePropertyTypes<Original, Updates>;
 * // Result is { a: number; b: number; c: string }
 * ```
 */
export type ReplaceProperties<Object, Updates extends Partial<Record<keyof Object, unknown>>> = Omit<
    Object,
    keyof Updates
> &
    Updates;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PartialRecord<K extends keyof any, T> = {
    [P in K]?: T;
};

export type ValueOf<T> = T[keyof T];
